export interface UsecureServiceFlag {
  key: string;
  label: string;
}

export interface UsecureCampaignEmailAsset {
  fromName?: string | null;
  fromAddress?: string | null;
  subject?: string | null;
  links?: string[];
  previewHtml?: string | null;
  previewImageUrl?: string | null;
  source?: string | null;
}

export interface UsecureCampaign {
  id: string;
  name: string;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  recipientCount: number;
  sentCount: number;
  openCount: number;
  visitCount: number;
  compromiseCount: number;
  reportCount: number;
  queuedCount?: number;
  deliveryMethod?: string | null;
  metrics?: {
    openRate: number;
    visitRate: number;
    compromiseRate: number;
    reportRate: number;
  };
  email?: UsecureCampaignEmailAsset;
}

export interface UsecureEmailVariant {
  label: string;
  subject: string;
  body: string;
}

export interface UsecureIncompleteTrainingUser {
  id?: string | null;
  name: string;
  email?: string | null;
  courses: string[];
}

export interface UsecureFollowupMetrics {
  trainingCompletionRate?: number;
  assignedCourses?: number;
  completedCourses?: number;
  incompleteCourses?: number;
  averageScore?: number;
  failedCourses?: number;
  clicks?: number;
  compromises?: number;
  reports?: number;
  openBreaches?: number;
  openHighBreaches?: number;
  highRiskUsers?: number;
  activeLearners?: number;
}

export interface UsecureMonthlyFollowup {
  subject: string;
  highlights: string[];
  recommendations: string[];
  incompleteTrainingUsers?: UsecureIncompleteTrainingUser[];
  emailVariants?: Record<'short' | 'long' | 'upsell', UsecureEmailVariant>;
  emailBody: string;
}

export interface UsecureProjectLink {
  projectId: number;
  projectTitle: string;
  clientId?: number | null;
  clientName?: string | null;
  portfolioName?: string | null;
  ownerName?: string | null;
  matchScore?: number | null;
  status: 'linked' | 'needs_validation' | 'confirmed_manual';
  url: string;
}

export interface UsecureServoComparison {
  clientName: string;
  matchedClientName?: string | null;
  matchedAlias?: string | null;
  matchScore?: number | null;
  servoUrl: string;
  status: 'matched' | 'no_cyber_referent' | 'unavailable';
  typeCyber?: boolean | null;
  prime?: string | null;
  referentCyber?: string | null;
  portfolioName?: string | null;
  projectLink?: UsecureProjectLink | null;
  error?: string | null;
}

export interface UsecureProjectTaskLink {
  taskId: number;
  projectId: number;
  title: string;
  status?: string | null;
  percent?: number | null;
  timeSpentMinutes?: number | null;
  dueDate?: string | null;
  url: string;
  source?: string;
  followupId?: number;
}

export interface UsecureClientFollowup {
  id: number;
  usecureCompanyId: string;
  clientName: string;
  periodMonth: string;
  status: 'draft' | 'ready' | 'sent' | 'responded' | 'action_created' | 'closed';
  health: { score: number; status?: string | null; inputs?: { trainingCompletionRate?: number; assignedCourses?: number; completedCourses?: number; incompleteCourses?: number; averageScore?: number; failedCourses?: number; clicks?: number; compromises?: number; reports?: number; openBreaches?: number; openHighBreaches?: number; highRiskUsers?: number; activeLearners?: number } };
  generatedSummary: { highlights?: string[]; recommendations?: string[]; signals?: string[]; metrics?: UsecureFollowupMetrics; incompleteTrainingUsers?: UsecureIncompleteTrainingUser[]; emailVariants?: Record<'short' | 'long' | 'upsell', UsecureEmailVariant> };
  generatedEmailSubject: string;
  generatedEmailBody: string;
  emailVariants?: Record<'short' | 'long' | 'upsell', UsecureEmailVariant>;
  advisorNotes?: string;
  clientSatisfaction?: number | null;
  clientQuestions?: string;
  nextAction?: string;
  nextFollowupDate?: string | null;
  sentAt?: string | null;
  sentEmailVariant?: { key: string; label?: string; subject?: string; body?: string; snapshot?: Record<string, unknown> } | null;
  sentEmailHistory?: Array<{ key?: string; label?: string; subject?: string; body?: string; sentAt?: string; [key: string]: unknown }>;
  confirmedProjectLink?: UsecureProjectLink | null;
  projectTaskLink?: UsecureProjectTaskLink | null;
  respondedAt?: string | null;
  closedAt?: string | null;
  servoComparison?: UsecureServoComparison | null;
}

export interface UsecureFollowupsResponse {
  periodMonth: string;
  followups: UsecureClientFollowup[];
}

export interface UsecurePrepareMonthlyResponse extends UsecureFollowupsResponse {
  generatedCount: number;
  createdTaskCount: number;
  existingTaskCount: number;
  needsValidationCount: number;
  missingProjectCount: number;
}

export interface UsecureTrendPoint {
  periodMonth: string;
  healthScore: number;
  healthStatus?: string | null;
  completionRate?: number | null;
  clicks?: number | null;
  compromises?: number | null;
  reports?: number | null;
  openBreaches?: number | null;
}

export interface UsecureTrend {
  direction: 'improving' | 'declining' | 'stable';
  scoreDelta: number;
  points: UsecureTrendPoint[];
  insights: string[];
  summary: string;
}

export interface UsecureClientReport {
  title: string;
  sections: Array<{ title: string; items: string[] }>;
  printHtml: string;
}

export interface UsecureClientReportResponse {
  followup: UsecureClientFollowup;
  trend: UsecureTrend;
  report: UsecureClientReport;
}

export interface UsecureClientCampaigns {
  usecureCompanyId: string;
  externalId?: string | null;
  name: string;
  accountType?: string | null;
  status?: string | null;
  services: UsecureServiceFlag[];
  plan?: {
    name?: string | null;
    learnerCount?: number;
    learnerLimit?: number;
    utilizationRate?: number;
    domainLock?: boolean;
    domain?: string | null;
  };
  userSummary?: { totalCount: number; verifiedCount: number; unverifiedCount: number };
  users?: Array<{ id?: string | null; name?: string | null; email?: string | null; firstName?: string | null; lastName?: string | null; inactive?: boolean | null; commsType?: string | null }>;
  learners?: Array<{
    id?: string | null;
    learnerId?: string | null;
    name?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    inactive?: boolean | null;
    simulationResults?: Array<{ id?: string | null; simulationId?: string | null; open?: string | null; visit?: string | null; compromise?: string | null; report?: string | null; createdAt?: string | null; deliveryMethod?: string | null }>;
  }>;
  dailyUsage?: Array<{ date?: string | null; maxLearners?: number | null }>;
  usage?: Array<{ startDate?: string | null; maxLearners?: number | null; [key: string]: unknown }>;
  learnerSummary?: { totalCount: number; activeCount: number; inactiveCount: number; highRiskCount: number; managersCount: number };
  courseSummary?: { assignedCount: number; completedCount: number; incompleteCount: number; completionRate: number; averageScore: number; failedCount: number };
  simulationSummary?: { totalCount: number; openCount: number; visitCount: number; compromiseCount: number; reportCount: number };
  breachSummary?: { totalCount: number; openCount: number; resolvedCount: number; openHighCount: number; openMediumCount: number };
  uBreachProEnabled?: boolean;
  uBreachProDomains?: string[];
  activeCampaigns: UsecureCampaign[];
  campaignCount: number;
  monthlyFollowup?: UsecureMonthlyFollowup;
}

export interface UsecureDashboard {
  connected: boolean;
  configured?: boolean;
  fromCache?: boolean;
  liveError?: string;
  generatedAt?: string;
  lastSyncAt?: string | null;
  summary: {
    clientWithActiveCampaignsCount: number;
    activeCampaignCount: number;
    sentTotal: number;
    averageVisitRate: number;
    averageReportRate: number;
    averageCompromiseRate: number;
  };
  clients: UsecureClientCampaigns[];
  schemaSummary?: {
    hasEmailAssetFields?: boolean;
    fields?: Array<{ name: string }>;
  } | null;
}

export interface UsecureEmailAssetSchemaProbe {
  checkedAt: string;
  introspectionDisabled?: boolean;
  message?: string;
  hasLikelyEmailAssetFields: boolean;
  simulationFields: Array<{ name: string; type?: string | null; looksLikeEmailAsset?: boolean }>;
  simulationEmailAssetFields: Array<{ name: string; type?: string | null; looksLikeEmailAsset?: boolean }>;
  candidates: Array<{
    typeName: string;
    kind?: string;
    matchingFields: Array<{ name: string; type?: string | null }>;
  }>;
}

async function readJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = (data as any)?.error || response.statusText || 'Erreur API uSecure';
    throw new Error(message);
  }
  return data as T;
}

export async function getUsecureDashboard(mode: 'live' | 'cache' = 'live'): Promise<UsecureDashboard> {
  const response = await fetch(`/api/prisme/cyber/usecure/dashboard?mode=${mode}`, { credentials: 'include' });
  return readJson<UsecureDashboard>(response);
}

export async function syncUsecureDashboard(): Promise<UsecureDashboard> {
  const response = await fetch('/api/prisme/cyber/usecure/sync', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  return readJson<UsecureDashboard>(response);
}

export async function saveUsecureCampaignEmailAsset(
  simulationId: string,
  payload: UsecureCampaignEmailAsset
): Promise<void> {
  const response = await fetch(`/api/prisme/cyber/usecure/campaigns/${encodeURIComponent(simulationId)}/email-asset`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  await readJson(response);
}

export async function getUsecureEmailAssetSchemaProbe(): Promise<UsecureEmailAssetSchemaProbe> {
  const response = await fetch('/api/prisme/cyber/usecure/schema/email-assets', { credentials: 'include' });
  return readJson<UsecureEmailAssetSchemaProbe>(response);
}

export async function getUsecureFollowups(periodMonth?: string): Promise<UsecureFollowupsResponse> {
  const query = periodMonth ? `?periodMonth=${encodeURIComponent(periodMonth)}` : '';
  const response = await fetch(`/api/prisme/cyber/usecure/followups${query}`, { credentials: 'include' });
  return readJson<UsecureFollowupsResponse>(response);
}

export async function generateUsecureFollowups(periodMonth?: string, usecureCompanyId?: string): Promise<UsecureFollowupsResponse> {
  const response = await fetch('/api/prisme/cyber/usecure/followups/generate', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ periodMonth, usecureCompanyId }),
  });
  return readJson<UsecureFollowupsResponse>(response);
}

export async function prepareUsecureMonthlyOperations(periodMonth?: string): Promise<UsecurePrepareMonthlyResponse> {
  const response = await fetch('/api/prisme/cyber/usecure/followups/prepare', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ periodMonth }),
  });
  return readJson<UsecurePrepareMonthlyResponse>(response);
}

export async function updateUsecureFollowup(
  id: number,
  payload: Partial<Pick<UsecureClientFollowup, 'status' | 'advisorNotes' | 'clientSatisfaction' | 'clientQuestions' | 'nextAction' | 'nextFollowupDate' | 'sentEmailVariant' | 'confirmedProjectLink'>>
): Promise<UsecureClientFollowup> {
  const response = await fetch(`/api/prisme/cyber/usecure/followups/${encodeURIComponent(id)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await readJson<{ followup: UsecureClientFollowup }>(response);
  return data.followup;
}

export async function searchUsecureProjectCandidates(query: string): Promise<UsecureProjectLink[]> {
  const response = await fetch(`/api/prisme/cyber/usecure/followups/projects/search?q=${encodeURIComponent(query)}`, { credentials: 'include' });
  const data = await readJson<{ projects: UsecureProjectLink[] }>(response);
  return data.projects || [];
}

export async function confirmUsecureFollowupProject(id: number, projectLink: UsecureProjectLink): Promise<UsecureClientFollowup> {
  return updateUsecureFollowup(id, { confirmedProjectLink: projectLink });
}

export async function createUsecureFollowupProjectTask(id: number): Promise<{ created: boolean; projectTaskLink: UsecureProjectTaskLink; followup: UsecureClientFollowup }> {
  const response = await fetch(`/api/prisme/cyber/usecure/followups/${encodeURIComponent(id)}/project-task`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  return readJson<{ created: boolean; projectTaskLink: UsecureProjectTaskLink; followup: UsecureClientFollowup }>(response);
}

export async function refreshUsecureProjectTaskLinks(periodMonth?: string): Promise<UsecureFollowupsResponse> {
  return getUsecureFollowups(periodMonth);
}

export async function getUsecureClientFollowupReport(id: number): Promise<UsecureClientReportResponse> {
  const response = await fetch(`/api/prisme/cyber/usecure/followups/${encodeURIComponent(id)}/report`, { credentials: 'include' });
  return readJson<UsecureClientReportResponse>(response);
}

export async function getUsecureClientTrend(usecureCompanyId: string): Promise<{ usecureCompanyId: string; trend: UsecureTrend; followups: UsecureClientFollowup[] }> {
  const response = await fetch(`/api/prisme/cyber/usecure/followups/client/${encodeURIComponent(usecureCompanyId)}/trend`, { credentials: 'include' });
  return readJson<{ usecureCompanyId: string; trend: UsecureTrend; followups: UsecureClientFollowup[] }>(response);
}
